import type { ListQuery } from './index'

export type ClientType = 'individual' | 'company' | 'property_management'

/** Commercial category — separate from Active/Inactive record status. */
export type ClientCategory = 'prospect' | 'client'

/** Client-list invoice payment badge — distinct from payment-record PaymentStatus. */
export type ClientPaymentStatus = 'normal' | 'pending' | 'late'

export interface ClientContact {
  id: string
  client_id?: string
  full_name: string | null
  role: string | null
  phone: string | null
  email: string | null
  sort_order: number
  created_at?: string
  updated_at?: string
}

export interface Client {
  id: string
  tenant_id: string
  // Human-readable, auto-generated client reference shown as "Client ID" (#22).
  reference?: string | null
  type: ClientType
  name: string
  /** Prospect vs converted Client — system-managed; omit or `client` hides the P badge. */
  category?: ClientCategory
  contacts?: ClientContact[]
  /** Present on nested quote/invoice payloads (primary contact email). */
  email?: string | null
  /** Present on some nested payloads (primary contact phone). */
  phone?: string | null
  siret: string | null
  siren: string | null
  vat_number: string | null
  country: string | null
  city: string | null
  postal_code: string | null
  address: string | null
  address_additional?: string | null
  latitude: number | null
  longitude: number | null
  notes: string | null
  consent_data: boolean
  status?: string | null
  source?: string | null
  property_type?: string | null
  access_code?: string | null
  floor?: string | null
  access_information?: string | null
  credit_status?: string | null
  /** Derived from invoices on the client list (Normal / Pending / Late). */
  payment_status?: ClientPaymentStatus | null
  latest_intervention_date?: string | null
  latest_intervention_value?: number | null
  total_revenue?: number | null
  outstanding_receivables?: number | null
  contracts_count?: number | null
  created_at: string
  updated_at: string
}

// "Chase Invoice" summary surfaced on the client detail (#21). Null when the
// client has no overdue / partially-paid invoice.
export interface ClientChaseSummary {
  invoice_id: string
  invoice_number: string | null
  status: 'overdue' | 'partially_paid' | string
  balance_due: number
  expected_payment_method: string | null
  due_date: string | null
}

export interface ClientDetail extends Client {
  assets_count: number
  open_invoices_total: number
  active_contracts_count: number
  /** Non-deleted, non-cancelled jobs for this client. */
  interventions_count: number
  /** Closest upcoming (planned / assigned / in progress) job date. */
  next_visit_date?: string | null
  chase?: ClientChaseSummary | null
  /** Display name of who last modified the client (activity log causer). */
  updated_by?: string | null
}

export type ClientAssetOrigin = 'manual' | 'report'

/** Labels allowed when creating / editing equipment manually. */
export const MANUAL_EQUIPMENT_TYPES = [
  'Bait Station',
  'Insect detector',
  'Mechanical Trap',
  'UV lamp',
  'Other',
] as const

export type ManualEquipmentType = (typeof MANUAL_EQUIPMENT_TYPES)[number]

export interface ClientAsset {
  id: string
  tenant_id: string
  client_id: string
  label: string
  asset_type: string | null
  location: string | null
  serial?: string | null
  installation_date?: string | null
  note?: string | null
  origin: ClientAssetOrigin
  /** @deprecated Legacy product fields — prefer serial / installation_date. */
  concentration?: string | null
  quantity?: string | number | null
  job_id?: string | null
  job_reference?: string | null
  source_report_url?: string | null
  delivery_date?: string | null
  meta: Record<string, unknown> | null
  created_at: string
  updated_at: string
}

export interface ActivityLog {
  id: string
  tenant_id: string
  subject_type: string
  subject_id: string
  causer_id: string | null
  event_type: string
  description: string | null
  // Status transition detail (e.g. "old -> new") exposed by ActivityLogResource.
  status_detail?: string | null
  // Map of changed fields { field: { old, new } } exposed by ActivityLogResource.
  changed_fields?: Record<string, { old: unknown; new: unknown }> | null
  // Acting user (causer_name) resolved server-side; null for system actions.
  user?: string | null
  timestamp?: string | null
  properties: Record<string, unknown> | null
  created_at: string
}

export interface ClientContactPayload {
  id?: string
  full_name?: string
  role?: string
  phone: string
  email?: string
}

export interface CreateClientRequest {
  type: ClientType
  name: string
  // Required by the API for business types (company / property_management).
  company_name?: string
  contacts: ClientContactPayload[]
  siret?: string
  siren?: string
  vat_number?: string
  country?: string
  city?: string
  postal_code?: string
  address?: string
  address_additional?: string
  latitude?: number
  longitude?: number
  source?: string
  status?: string
  notes?: string
  consent_data?: boolean
  property_type?: string
  access_code?: string
  floor?: string
  access_information?: string
}
export type EditClientRequest = Partial<CreateClientRequest>
export interface UpdateNotesRequest {
  notes: string
}
export interface ClientAssetRequest {
  label: ManualEquipmentType | string
  location?: string | null
  serial?: string | null
  installation_date?: string | null
  note?: string | null
}

export interface ClientAssetListQuery extends ListQuery {
  origin?: ClientAssetOrigin
}
export interface ClientListQuery extends ListQuery {
  type?: ClientType
  status?: string
  source?: string
  created_from?: string
  created_to?: string
  has_revenue?: string | boolean
  has_outstanding_balance?: string | boolean
  has_active_contracts?: string | boolean
}

// Client list KPI summary (Figma 2881:33957). Tenant-wide aggregates.
export interface ClientSummary {
  customers: number
  business_customers: number
  individual_customers: number
  revenue: number
  billed_customers: number
  to_be_cashed: number
  to_be_cashed_customers: number
  contracts_follow_up: number
  contracts_follow_up_customers: number
}

// Client document row (2.4.14). Mirrors the seed Documentable shape.
export interface Documentable {
  id: string
  tenant_id: string
  file_id: string
  documentable_type: string
  documentable_id: string
  doc_type?: string | null
  file_name: string
  file_type: string | null
  file_size: number | null
  url: string | null
  uploaded_by: string | null
  uploaded_by_name: string | null
  created_at: string
  updated_at: string
}

export interface ExportLink {
  url: string
  filename?: string
  expires_at?: string | null
}
